/**
 * nav-live(2026-10-24) — 헤더 조회 수 카운터의 순수 함수(서버·클라이언트 공용 · 훅·DB 없음).
 *
 * 왜: 헤더 지역 셀렉 자리에 「● LIVE 최근 30일 조회 N회」 를 실측으로 보여 준다(Node 요청 · 최종 사양).
 *   · 원천 = rpc nav_pv_30d() — 굴러가는 30일 page_views human 행 수(같은 사람의 반복 조회 포함).
 *     human 판정을 빠져나간 자동화(Headless 등 UA 표지 · KST 하루 50회 초과 visitor)는 SQL 에서 뺀다. 가공·바닥값·부풀림 0.
 *   · 값이 없으면(0·실패) 아무것도 그리지 않는다 — 가짜 숫자를 만들지 않는다.
 */

/** API 응답 CDN 캐시 — 60초 신선 · 300초 동안 낡은 값 즉시 응답 + 뒤에서 갱신. 봇·사람 모두 같은 캐시를 탄다(DB 부하 추가 0). */
// 10/26 Node 「실시간 반영」 — CDN 15초 · 낡은 값 허용 15초(종전 60/300 → 최대 6분 늦음). rpc 155ms · 15초 1회 = 분당 4회.
export const VISITORS_CACHE_CONTROL = 'public, s-maxage=15, stale-while-revalidate=15';
/** 실패 응답은 짧게만 캐시(10초) — 장애가 길게 박히지 않게. */
export const VISITORS_FAIL_CACHE_CONTROL = 'public, s-maxage=10';

/** rpc 응답(jsonb 객체 · 혹시 배열이면 첫 행)·API 응답 → 30일 조회 수. 숫자 아님·음수는 null. */
export function parsePv30d(data: unknown): number | null {
  const row = Array.isArray(data) ? data[0] : data;
  if (!row || typeof row !== 'object') return null;
  const v = (row as Record<string, unknown>).pv_30d;
  const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() !== '' ? Number(v) : Number.NaN;
  return Number.isFinite(n) && n >= 0 ? Math.floor(n) : null;
}

/** 천 단위 쉼표(로케일 무관 고정 — 서버·브라우저가 같은 글자를 낸다). */
export function formatCount(n: number): string {
  return String(Math.floor(n)).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/**
 * live-v3(2026-11-02 · Node 「오늘 방문자 + 30일 누적 방문자 · A안」) — rpc nav_visitors() 응답 → 두 숫자.
 *   today = KST 오늘 0시부터 순 방문자(visitor_id) · cum = 지난 30일 «날마다 센 순 방문자» 의 합(30일 누적 방문자).
 *   봇·자동화 제외 기준은 nav_pv_30d 와 같다. 둘 중 하나라도 숫자가 아니면 null(가짜 숫자 0).
 */
export interface VisitorCounts { today: number; cum: number }
export function parseVisitors(data: unknown): VisitorCounts | null {
  const row = Array.isArray(data) ? data[0] : data;
  if (!row || typeof row !== 'object') return null;
  const num = (v: unknown) => { const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() !== '' ? Number(v) : Number.NaN; return Number.isFinite(n) && n >= 0 ? Math.floor(n) : null; };
  const r = row as Record<string, unknown>;
  const today = num(r.today_uv), cum = num(r.cum_30d_uv);
  return today == null || cum == null ? null : { today, cum };
}

export interface VisitorView {
  /** 오늘 순 방문자 숫자(굴리기용) */
  n: number;
  /** 「678」 */
  today: string;
  /** 「8,732」 */
  cum: string;
  /** 스크린리더·title 용 전체 문장. */
  label: string;
}

/** 30일 누적이 1 이상일 때만 보인다(0·null → 숨김). 오늘은 0 이어도 보인다(자정 직후). */
export function visitorView(c: VisitorCounts | null | undefined): VisitorView | null {
  if (!c || !Number.isFinite(c.cum) || c.cum < 1 || !Number.isFinite(c.today) || c.today < 0) return null;
  const today = formatCount(c.today), cum = formatCount(c.cum);
  return { n: c.today, today, cum, label: `실시간 집계 오늘 조회수 ${today}회 · 30일 누적 조회수 ${cum}회` }; // live-v5 · Node 「조회수로」
}
